'use client';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import type { KeyboardEvent } from 'react';
import { useId, useState } from 'react';

import { ProductRail } from '@eliadmin/core/src/components/dlz/home/products/product-rail';
import { TabBar, TabButton } from '@eliadmin/core/src/components/dlz/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type ProductTab = { key: string; label: string; products: Product[] };

const KEY_TARGET: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_, count) => count - 1,
};

/**
 * Tabs with automatic activation (arrows, Home / End) over product rails – on phones a 2 × 2 card
 * grid under a left-aligned, sideways scrolling tab bar (Figma 60367:38717). Every list is loaded on
 * the server and all panels share one grid cell, so switching is instant and the section keeps the
 * height of the tallest panel.
 */
export function ProductTabs({ label, tabs }: { label: string; tabs: ProductTab[] }) {
  const baseId = useId();
  const [active, setActive] = useState(tabs[0]?.key);
  const tabId = (key: string) => `${baseId}-tab-${key}`;
  const panelId = (key: string) => `${baseId}-panel-${key}`;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const target = KEY_TARGET[event.key];
    if (!target) return;

    event.preventDefault();
    const index = tabs.findIndex(tab => tab.key === active);
    const next = tabs[target(index, tabs.length)];
    setActive(next.key);
    document.getElementById(tabId(next.key))?.focus();
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Key events bubble up from the focused tab. */}
      <div className="flex md:justify-center" onKeyDown={onKeyDown}>
        <TabBar label={label}>
          {tabs.map(tab => (
            <TabButton
              key={tab.key}
              id={tabId(tab.key)}
              controls={panelId(tab.key)}
              selected={tab.key === active}
              onSelect={() => setActive(tab.key)}
            >
              {tab.label}
            </TabButton>
          ))}
        </TabBar>
      </div>
      <div className="grid grid-cols-1">
        {tabs.map(tab => (
          <div
            key={tab.key}
            role="tabpanel"
            id={panelId(tab.key)}
            aria-labelledby={tabId(tab.key)}
            className={cn('col-start-1 row-start-1', tab.key !== active && 'invisible')}
          >
            <ProductRail products={tab.products} label={tab.label} layout="grid" />
          </div>
        ))}
      </div>
    </div>
  );
}
